import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Knowledge → Gap Report (managers/owner): what staff asked that the docs
// couldn't answer. Aggregate only — role and location, never names. Resolving
// a gap (with a note naming the doc that answers it) removes it from the list.

export function KbGapReportView({ user }) {
  const headers = { 'x-user-id': String(user.id) };
  const [report, setReport] = useState(null);
  const [drip, setDrip] = useState(null);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState({});

  async function load() {
    try { setReport(await api('GET', '/api/kb/reports/gaps', null, headers)); }
    catch (e) { setError(e.message || 'Failed to load gap report'); }
    // Drip rollup (aggregate-only) — quiet if drip is off or empty.
    try { setDrip(await api('GET', '/api/drip/report?days=30', null, headers)); }
    catch { /* panel just doesn't render */ }
  }
  useEffect(() => { load(); }, []);

  const topicName = (slug) => String(slug).replace(/[-_]/g, ' ');

  async function resolve(id) {
    const note = (notes[id] || '').trim();
    if (!note) return alert('Add a note first — which doc answers this now?');
    try { await api('POST', `/api/kb/gaps/${id}/resolve`, { note }, headers); load(); }
    catch (e) { alert(e.message || 'Resolve failed'); }
  }

  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-xs text-red-700">{error}</div></div>;
  if (!report) return <div className="card">Loading…</div>;

  return (
    <div className="space-y-3">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        {[
          { k: report.askVolume, l: 'Questions asked' },
          { k: report.solvedRate === null ? '—' : `${report.solvedRate}%`, l: 'Marked “solved it”' },
          { k: `${report.gapQuestions.length} open`, l: `Gaps · ${report.resolvedThisWeek} resolved` },
        ].map((s) => (
          <div key={s.l} className="card" style={{ borderTop: '3px solid var(--gold)' }}>
            <div className="serif" style={{ fontSize: 26, fontWeight: 700 }}>{s.k}</div>
            <div className="label-uppercase" style={{ marginTop: 4 }}>{s.l}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="text-base font-semibold" style={{ marginBottom: 8 }}>Open gaps — someone should write these docs</div>
        {report.gapQuestions.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No open gaps. 🎉</div>
        )}
        {report.gapQuestions.map((g) => (
          <div key={g.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>“{g.question}”</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', fontFamily: 'monospace', margin: '2px 0 6px' }}>
              {g.role || '?'} · {g.location || '?'}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={notes[g.id] || ''} onChange={(e) => setNotes({ ...notes, [g.id]: e.target.value })}
                placeholder="Resolved by… (doc slug)" style={{ flex: 1, maxWidth: 260, padding: '5px 8px', fontSize: 12 }} />
              <button className="btn" style={{ fontSize: 11 }} onClick={() => resolve(g.id)}>Resolve</button>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="text-base font-semibold" style={{ marginBottom: 8 }}>Top questions this week</div>
        {report.topQuestions.map((q, i) => (
          <div key={i} style={{ padding: '5px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>({q.times}×)</span> {q.question}
            <span style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginLeft: 6 }}>{q.roles} · {q.locations}</span>
          </div>
        ))}
        {report.topQuestions.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No questions this week yet.</div>
        )}
      </div>

      {drip?.enabled && drip.reach?.cards > 0 && (
        <div className="card">
          <div className="text-base font-semibold" style={{ marginBottom: 2 }}>Knowledge Drip — last {drip.window_days} days</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
            {drip.reach.people} people · {drip.reach.cards} cards seen · {drip.reach.answers} questions answered.
            Aggregate only — never per person. Topics need {drip.min_answers}+ answers to show a score.
          </div>

          <div className="label-uppercase" style={{ marginBottom: 4 }}>Shakiest topics</div>
          {drip.topics.filter((t) => t.pct_correct !== null).slice(0, 6).map((t) => (
            <div key={t.doc_slug} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <span style={{ flex: 1, textTransform: 'capitalize' }}>{topicName(t.doc_slug)}</span>
              <span style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>{t.answers} answers</span>
              <span style={{ fontWeight: 700, minWidth: 42, textAlign: 'right',
                color: t.pct_correct < 60 ? '#c62828' : t.pct_correct < 80 ? '#b26a00' : '#2e7d32' }}>
                {t.pct_correct}%
              </span>
            </div>
          ))}
          {drip.topics.every((t) => t.pct_correct === null) && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Cards are being seen, but no topic has enough question answers to score yet.
            </div>
          )}

          {drip.shakiest_cards.length > 0 && (
            <>
              <div className="label-uppercase" style={{ margin: '10px 0 4px' }}>Shakiest cards</div>
              {drip.shakiest_cards.map((c) => (
                <div key={c.atom_slug} style={{ padding: '3px 0', fontSize: 12.5 }}>
                  <span style={{ fontWeight: 600, color: c.pct_correct < 60 ? '#c62828' : 'inherit' }}>{c.pct_correct}%</span>
                  {' '}<span style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.atom_slug}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginLeft: 6 }}>{c.answers} answers · {topicName(c.doc_slug)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
